
import { useState, useCallback, useEffect, useRef } from 'react';
import { savePremiumProgress, loadPremiumProgress } from '@/services/premiumProgressService';
import { supabase } from '@/integrations/supabase/client';

/**
 * A hook that manages assessment responses in localStorage
 * and syncs to DB for authenticated premium users.
 */
export const useLocalStorageResponses = (assessmentId: string) => {
  const storageKey = `assessment_${assessmentId}_responses`;
  const dbSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasLoadedFromDb = useRef(false);
  
  const [responses, setResponsesState] = useState<Record<string, any>>(() => {
    try {
      const storedResponses = localStorage.getItem(storageKey);
      return storedResponses ? JSON.parse(storedResponses) : {};
    } catch (error) {
      console.error('Error loading responses from localStorage:', error);
      return {};
    }
  });

  // On mount, if localStorage is empty, try loading from DB (for cross-session persistence)
  useEffect(() => {
    if (hasLoadedFromDb.current) return;
    hasLoadedFromDb.current = true;

    const loadFromDb = async () => {
      try {
        const storedLocal = localStorage.getItem(storageKey);
        if (storedLocal) {
          const parsed = JSON.parse(storedLocal);
          if (Object.keys(parsed).length > 0) return; // localStorage has data, skip DB
        }

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const progress = await loadPremiumProgress(assessmentId);
        if (progress && progress.responses && Object.keys(progress.responses).length > 0) {
          console.log('[useLocalStorage] Restoring', Object.keys(progress.responses).length, 'responses from DB');
          setResponsesState(progress.responses);
          localStorage.setItem(storageKey, JSON.stringify(progress.responses));
        }
      } catch (err) {
        console.error('[useLocalStorage] Error loading from DB:', err);
      }
    };

    loadFromDb();
  }, [assessmentId, storageKey]);

  // Debounced sync to DB
  const syncToDb = useCallback((updatedResponses: Record<string, any>) => {
    if (dbSyncTimerRef.current) clearTimeout(dbSyncTimerRef.current);
    dbSyncTimerRef.current = setTimeout(() => {
      savePremiumProgress(assessmentId, updatedResponses).catch(err => {
        console.warn('[useLocalStorage] DB sync failed:', err);
      });
    }, 2000); // 2s debounce
  }, [assessmentId]);
  
  const setResponses = useCallback((newResponses: any) => {
    try {
      const updatedResponses = typeof newResponses === 'function' 
        ? newResponses(responses) 
        : newResponses;
      
      setResponsesState(updatedResponses);
      
      setTimeout(() => {
        try {
          localStorage.setItem(storageKey, JSON.stringify(updatedResponses));
        } catch (error) {
          console.error('Error saving responses to localStorage:', error);
        }
      }, 0);

      // Also sync to DB (debounced)
      syncToDb(updatedResponses);
    } catch (error) {
      console.error('Error updating responses:', error);
    }
  }, [responses, storageKey, syncToDb]);
  
  const clearStoredResponses = useCallback(() => {
    setResponsesState({});
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error clearing responses from localStorage:', error);
    }
    // Also clear from DB
    import('@/services/premiumProgressService').then(({ deletePremiumProgress }) => {
      deletePremiumProgress(assessmentId).catch(() => {});
    });
  }, [storageKey, assessmentId]);
  
  return {
    responses,
    setResponses,
    clearStoredResponses,
  };
};
